'use client';

import type { BookingTypeSummary } from './types';
import { formatDuration, formatPrice } from './types';

export function BookingTypePicker({
  types,
  selectedSlug,
  onSelect,
}: {
  types: BookingTypeSummary[];
  selectedSlug: string | null;
  onSelect: (slug: string) => void;
}) {
  return (
    <ul className="grid grid-cols-1 gap-4 md:gap-5" role="radiogroup" aria-label="Booking type">
      {types.map((t) => {
        const selected = selectedSlug === t.slug;
        const free = t.priceCents === null;
        return (
          <li key={t.slug}>
            <button
              type="button"
              role="radio"
              aria-checked={selected}
              onClick={() => onSelect(t.slug)}
              className="w-full text-left transition-all duration-200"
              style={{
                background: 'var(--bg-elevated)',
                border: selected
                  ? `1px solid ${t.color}`
                  : '1px solid var(--border)',
                borderRadius: 'var(--radius-2xl)',
                padding: 24,
                boxShadow: selected
                  ? `inset 0 0 0 1px ${t.color}, 0 0 32px ${t.color}33`
                  : 'none',
                cursor: 'pointer',
              }}
            >
              <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2.5 mb-2">
                    <span
                      aria-hidden
                      style={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        background: t.color,
                        boxShadow: `0 0 0 4px ${t.color}1f`,
                        flexShrink: 0,
                      }}
                    />
                    <span
                      className="font-mono"
                      style={{
                        fontSize: 10,
                        fontWeight: 700,
                        letterSpacing: '0.14em',
                        color: t.color,
                        textTransform: 'uppercase',
                      }}
                    >
                      {t.slug.replace(/-/g, ' ')}
                    </span>
                    {!free && (
                      <span
                        className="font-mono rounded-full"
                        style={{
                          background: 'var(--bg-subtle)',
                          color: 'var(--text-secondary)',
                          fontSize: 9,
                          fontWeight: 700,
                          letterSpacing: '0.1em',
                          padding: '2px 8px',
                          border: '1px solid var(--border)',
                        }}
                      >
                        DEPOSIT
                      </span>
                    )}
                  </div>
                  <h3
                    className="font-sans text-text"
                    style={{
                      fontSize: 18,
                      fontWeight: 800,
                      letterSpacing: '-0.01em',
                      lineHeight: 1.2,
                    }}
                  >
                    {t.name}
                  </h3>
                  <p
                    className="text-text-secondary mt-2"
                    style={{ fontSize: 14, lineHeight: 1.65 }}
                  >
                    {t.description}
                  </p>
                </div>
                <div className="flex md:flex-col items-end md:items-end gap-2 md:gap-1 md:min-w-[120px] md:text-right">
                  <div
                    className="font-mono"
                    style={{
                      fontSize: 28,
                      fontWeight: 800,
                      lineHeight: 1,
                      letterSpacing: '-0.02em',
                      color: free ? 'var(--accent)' : 'var(--text-primary)',
                    }}
                  >
                    {formatPrice(t.priceCents)}
                  </div>
                  <div
                    className="font-mono inline-flex items-center gap-1.5"
                    style={{
                      fontSize: 11,
                      color: 'var(--text-muted)',
                      letterSpacing: '0.04em',
                    }}
                  >
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <circle cx="12" cy="12" r="10" />
                      <polyline points="12 6 12 12 16 14" />
                    </svg>
                    {formatDuration(t.durationMin)}
                  </div>
                </div>
              </div>
            </button>
          </li>
        );
      })}
    </ul>
  );
}
